/**
 * The rung of a Team's Plan line (`staff.plan`): Supervised brings Reeve's
 * plan back for a person's sign-off, Autonomous has Reeve approve it and the
 * build follows. The ticket sheet reads it so no sentence promises a sign-off
 * an Autonomous line will never ask for.
 *
 * One read of `GET /api/desks/:id/jobs` per Team a minute, shared by every
 * sheet that asks. Null while it loads, when the Team has no Plan line,
 * and when the read fails: the sentences then keep the sign-off words they
 * always had, rather than guess.
 */
import { useEffect, useState } from "react";
import type { JobAuthority } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { lineOf } from "./allTeams.js";
import { fetchTeamJobs } from "./teamsApi.js";

/** How long one read answers for: a rung changed in Team settings shows within a minute. */
const FRESH_MS = 60_000;

const reads = new Map<string, { at: number; read: Promise<JobAuthority | null> }>();

function readPlanAuthority(deskId: string): Promise<JobAuthority | null> {
  const cached = reads.get(deskId);
  if (cached && Date.now() - cached.at < FRESH_MS) return cached.read;
  const read = fetchTeamJobs(deskId)
    .then((body) => {
      const jobs = (body?.engagements ?? []).flatMap((group) => group.jobs ?? []);
      return lineOf(jobs, "staff.plan")?.authority ?? null;
    })
    .catch((err: unknown) => {
      // Forget the failure, so the next sheet asks again.
      reads.delete(deskId);
      captureUiError(err, {
        area: "Teams",
        operation: "plan_line_authority_read",
        deskId,
        severity: "warning",
      });
      return null;
    });
  reads.set(deskId, { at: Date.now(), read });
  return read;
}

/** Test seam: forget every cached read. */
export function resetPlanLineAuthorityCache(): void {
  reads.clear();
}

export function usePlanLineAuthority(deskId: string | null | undefined): JobAuthority | null {
  const [authority, setAuthority] = useState<{ deskId: string; value: JobAuthority | null } | null>(
    null,
  );
  useEffect(() => {
    if (!deskId) return;
    let alive = true;
    void readPlanAuthority(deskId).then((value) => {
      if (alive) setAuthority({ deskId, value });
    });
    return () => {
      alive = false;
    };
  }, [deskId]);
  return deskId && authority?.deskId === deskId ? authority.value : null;
}
